const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';

interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
  /** Set by guards / services when the failure is structured (e.g.
   *  MODULE_NOT_SUBSCRIBED, SMTP_NOT_CONFIGURED). */
  errorCode?: string;
  /** Module codes the endpoint requires (paired with errorCode). */
  requiredModules?: string[];
  /** Alternate message for admins — used when the default message points at
   *  an action only admins can take. Currently set on SMTP_NOT_CONFIGURED. */
  adminMessage?: string;
  /** Pus de client când a afișat el însuși mesajul (toast), ca apelantul să nu-l repete.
   *  Lipsește pe calea 401 → refresh → reîncercare, unde nu se afișează nimic. */
  shownToUser?: boolean;
}

/** Pages where we should NOT hard-redirect on 401.
 *  /accept-invitation is included so a re-invited user with stale cookies
 *  (deactivated/zombie account) doesn't get bounced to /login the moment
 *  auth-store.initialize() fires its first /auth/profile call. */
const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/accept-invitation', '/auth/magic'];

/** Endpoints where a 401 IS the real answer (no token to refresh).
 *  We must NOT mask the backend message with a generic "Sesiune expirată". */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password', '/auth/verify-email', '/auth/resend-verification'];

function isAuthEndpoint(endpoint: string): boolean {
  return AUTH_ENDPOINTS.some((p) => endpoint.startsWith(p));
}

/** Calea din API a unei adrese întregi (`…/api/v1/auth/login` → `/auth/login`), pentru `isAuthEndpoint`. */
function caleaInApi(url: string): string {
  const i = url.indexOf('/api/v1/');
  return i < 0 ? url : url.slice(i + '/api/v1'.length);
}

function isOnAuthPage(): boolean {
  if (typeof window === 'undefined') return false;
  return AUTH_PAGES.some((page) => window.location.pathname.startsWith(page));
}

/** Rezultatul unui refresh: reușit, eșuat sau reușit dar pe ALTĂ identitate (alt cont / altă firmă). */
type RefreshResult = 'ok' | 'failed' | 'session-changed';

/**
 * Pagina pleacă pe o sesiune nouă: cererea nu pleacă. 401 („oprește, nu salva”), nu 409 — în aplicație
 * 409 înseamnă conflict/duplicat (factura „modificată de alt utilizator”, vânzare POS eșuată definitiv).
 */
function sessionChangedError(): ApiError {
  return {
    statusCode: 401,
    error: 'SESSION_CHANGED',
    message: 'Sesiunea s-a schimbat. Pagina se reîncarcă.',
  };
}

class ApiClient {
  /**
   * Single-flight refresh: shared promise across concurrent tryRefresh() calls.
   * The backend blacklists the refresh token on rotation, so parallel refresh
   * calls would race and all-but-one would get 401. By deduplicating them here,
   * only one POST /auth/refresh is in flight at a time and all callers wait
   * for the same result.
   */
  private refreshPromise: Promise<RefreshResult> | null = null;

  /**
   * Hardening multi-tab (2026-06-11): după fiecare refresh reușit, identitatea
   * din răspuns (user/tenant) e raportată aici. auth-store înregistrează
   * callback-ul la bootstrap și forțează resync/reload dacă sesiunea din
   * cookie nu mai corespunde identității afișate în UI (race-ul «tabul vechi
   * rescrie cookie-urile peste login-ul nou»). Callback injectat (nu import)
   * ca să nu creăm dependență shared → features. Întoarce `true` când identitatea diferă de cea
   * afișată: pagina se reîncarcă, iar cererile care așteptau refresh-ul NU se mai reiau.
   */
  onRefreshIdentity: ((identity: { userId: string | null; tenantId: string | null }) => boolean) | null = null;

  /**
   * `true` din momentul în care pagina pleacă pe o sesiune nouă (alt cont / altă firmă / deconectare,
   * setat și de auth-store la plecare): nicio cerere nu mai pleacă din ea. Buclele care trăiesc peste
   * pagină (coada POS, importul CSV) ar trimite altfel, până la descărcare, sub identitatea nouă.
   */
  sessionChanged = false;

  async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (this.sessionChanged) throw sessionChangedError();
    const url = `${API_URL}/api/v1${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Detect read-only (GET) vs deliberate action (POST/PATCH/DELETE/PUT).
    // Pentru fetch-uri silente (GET la load) NU afișez toast 402 — userul
    // n-a făcut nimic deliberat. Pentru acțiuni deliberate (click, submit),
    // afișez toast cu mesajul de upgrade.
    const httpMethod = ((options.method as string | undefined) ?? 'GET').toUpperCase();
    const isReadOnlyRequest = httpMethod === 'GET' || httpMethod === 'HEAD';

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      // For auth endpoints (login/register/refresh/etc.), 401 IS the answer.
      // Don't attempt token refresh and don't mask with "Sesiune expirată" —
      // propagate the backend's real message ("Email sau parolă incorectă",
      // "Contul este dezactivat", etc.) so the user sees what's wrong.
      if (isAuthEndpoint(endpoint)) {
        const error = await response.json().catch(() => ({
          statusCode: 401,
          message: 'Autentificare eșuată',
        }));
        throw error as ApiError;
      }

      // Read the original 401 body upfront so we can fall back to its
      // real message if the refresh-token retry fails.
      const originalError = await response.clone().json().catch(() => null);

      // Try to refresh token (deduplicated across concurrent callers)
      const refreshed = await this.tryRefresh();
      if (refreshed === 'session-changed') {
        // Cookie-ul poartă acum alt cont sau altă firmă: pagina se reîncarcă pe sesiunea nouă.
        // Cererea NU se reia — o salvare ar ajunge în altă firmă.
        throw sessionChangedError();
      }
      if (refreshed === 'ok') {
        // Pagina a plecat pe altă sesiune cât a durat reîmprospătarea: reluarea ar pleca sub identitatea nouă.
        if (this.sessionChanged) throw sessionChangedError();
        const retryResponse = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
        // Răspunsul reîncercării se tratează exact ca unul direct (redirect, mesaje, marcaj).
        if (!retryResponse.ok) return this.failWith(retryResponse, isReadOnlyRequest);
        return this.parseSuccessBody<T>(retryResponse, isReadOnlyRequest);
      } else {
        // Only redirect to /login if NOT already on an auth page
        // This prevents the infinite reload loop
        if (typeof window !== 'undefined' && !isOnAuthPage()) {
          window.location.href = '/login';
        }
        throw (originalError ?? { statusCode: 401, message: 'Sesiune expirată' }) as ApiError;
      }
    }

    if (!response.ok) return this.failWith(response, isReadOnlyRequest);

    return this.parseSuccessBody<T>(response, isReadOnlyRequest);
  }

  /**
   * Un răspuns cu eroare, direct sau al reîncercării de după reînnoirea sesiunii: redirect la
   * re-acceptarea termenilor, mesajul refuzurilor pe care clientul le afișează singur (marcate
   * `shownToUser`, ca apelantul să nu le repete) și aruncarea erorii. Mesajele apar doar la
   * acțiuni (nu la GET): la o încărcare utilizatorul n-a făcut nimic.
   */
  private async failWith(response: Response, isReadOnlyRequest: boolean): Promise<never> {
    const error = await response.json().catch(() => ({
      statusCode: response.status,
      message: 'Eroare de server',
    }));
    // Subscription gate (ModuleGuard) — surface a single, consistent toast
    // so callers don't each have to format their own message. Component
    // can still inspect `errorCode === 'MODULE_NOT_SUBSCRIBED'` and offer
    // an "upgrade plan" CTA if the surrounding context warrants it.
    if (response.status === 403 && (error as ApiError).error === 'CONSENT_REACCEPTANCE_REQUIRED') {
      // Termenii au fost actualizați — redirect la pagina de re-acceptare.
      // Evităm redirect dacă suntem deja pe /re-acceptance (loop) sau pe pagini auth.
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        if (!path.startsWith('/re-acceptance') && !isOnAuthPage()) {
          window.location.href = '/re-acceptance';
        }
      }
      throw error as ApiError;
    }

    // Mod suport (admin în impersonare): backend-ul blochează scrierile fiscale +
    // plăți/echipă/date-firmă cu 403 IMPERSONATION_READONLY. Afișăm un toast consistent
    // pe orice încercare de scriere (nu pe GET), ca adminul să înțeleagă imediat — fără
    // să decorăm fiecare buton din zecile de module afectate. Backend-ul rămâne autoritar.
    if (
      response.status === 403 &&
      (error as ApiError).error === 'IMPERSONATION_READONLY' &&
      !isReadOnlyRequest
    ) {
      try {
        const { toast } = await import('sonner');
        toast.error((error as ApiError).message ?? 'Modificare dezactivată în modul suport (admin).', {
          id: 'impersonation-readonly',
          duration: 6000,
        });
        (error as ApiError).shownToUser = true;
      } catch {
        /* toast best-effort */
      }
      throw error as ApiError;
    }

    if (response.status === 402 && (error as ApiError).errorCode === 'MODULE_NOT_SUBSCRIBED' && !isReadOnlyRequest) {
      // Skip toast pentru GET (fetch silent la load) — user n-a făcut nimic deliberat.
      // Afișez doar pentru POST/PATCH/DELETE (click pe buton, submit form).
      try {
        const { toast } = await import('sonner');
        // Deduplicare: folosesc id derivat din modulele cerute astfel încât
        // toast-urile pentru același modul se înlocuiesc, nu se duplică
        // (când 2 componente fac fetch paralel pe endpoint-uri ale aceluiași modul).
        const required = ((error as ApiError & { requiredModules?: string[] }).requiredModules || []).join('-');
        const toastId = `module-not-subscribed-${required || 'unknown'}`;
        toast.error((error as ApiError).message ?? 'Modulul nu este activ în abonament.', {
          id: toastId,
          action: {
            label: 'Vezi planurile',
            onClick: () => {
              if (typeof window !== 'undefined') {
                window.location.href = '/preturi';
              }
            },
          },
          duration: 7000,
        });
        (error as ApiError).shownToUser = true;
      } catch {}
    }
    // Paid plan gate (PlanGuard) — free_permanent încearcă să acceseze
    // features paid-only (SAF-T, bank, multi-firmă; e-Factura NU — inclusă și în Free din 2026-07-22). Show toast
    // cu link spre upgrade.
    if (response.status === 402 && (error as ApiError).errorCode === 'PAID_PLAN_REQUIRED' && !isReadOnlyRequest) {
      try {
        const { toast } = await import('sonner');
        toast.error((error as ApiError).message ?? 'Această funcționalitate necesită un plan plătit.', {
          action: { label: 'Vezi planurile', onClick: () => { if (typeof window !== 'undefined') window.location.href = '/setari?tab=billing'; } },
          duration: 8000,
        });
        (error as ApiError).shownToUser = true;
      } catch {}
    }
    // Monthly limit reached (free_permanent: 10 facturi/lună hard block) —
    // surfacing toast cu CTA upgrade.
    if (response.status === 402 && (error as ApiError).errorCode === 'MONTHLY_LIMIT_REACHED' && !isReadOnlyRequest) {
      try {
        const { toast } = await import('sonner');
        toast.error((error as ApiError).message ?? 'Limita lunară atinsă. Activează planul plătit pentru nelimitat.', {
          action: { label: 'Vezi planurile', onClick: () => { if (typeof window !== 'undefined') window.location.href = '/setari?tab=billing'; } },
          duration: 8000,
        });
        (error as ApiError).shownToUser = true;
      } catch {}
    }
    // SMTP not configured — backend ships two messages: `message` (for
    // non-admin: "contact admin") and `adminMessage` (for admins: "go to
    // Settings → Email"). We pick based on the caller's permissions so
    // the toast points at an action they can actually take. Done here
    // (not in each caller) so the UX is consistent everywhere email is
    // triggered (invoice, reminder, receipt, etc.). SMTP_SEND_FAILED = SMTP configurat, dar refuzat la
    // trimitere (parolă greșită, server inaccesibil…): aceleași două mesaje, traduse de server.
    const smtpCode = (error as ApiError).errorCode;
    if (response.status === 400 && (smtpCode === 'SMTP_NOT_CONFIGURED' || smtpCode === 'SMTP_SEND_FAILED') && !isReadOnlyRequest) {
      try {
        const { toast } = await import('sonner');
        const { useAuthStore } = await import('@/features/auth/stores/auth-store');
        const perms = useAuthStore.getState().user?.permissions ?? [];
        const isAdmin = perms.includes('settings.company');
        const msg = isAdmin
          ? ((error as ApiError & { adminMessage?: string }).adminMessage ?? (error as ApiError).message)
          : (error as ApiError).message;
        toast.error(msg ?? 'Email-ul firmei nu este configurat.');
        (error as ApiError).shownToUser = true;
      } catch {}
    }
    throw error as ApiError;
  }

  /**
   * Parsează corpul unui răspuns de succes. Folosit ȘI pe calea normală, ȘI pe
   * cea de retry după refresh de sesiune — altfel cele două tratează diferit
   * exact același răspuns.
   *
   * `response.json()` direct ar arunca în Safari pe corp gol („The string did not
   * match the expected pattern"), deci îl tratăm noi. Discriminăm după metodă,
   * fiindcă golul înseamnă lucruri diferite:
   *  - GET/HEAD: NestJS serializează un handler care întoarce `null` ca 200 cu
   *    corp gol (`isNil(body) → res.send()`), deci golul înseamnă „resursa nu
   *    există" → `null`. Un `{}` ar fi truthy și ar face să cedeze orice gardă
   *    `if (data)` din UI (incident 2026-08-16: terminalul POS s-a randat peste
   *    o casierie inexistentă, apoi a trimis `?sessionId=undefined` → 22P02).
   *  - mutații: rămâne `{}` — cazul original al fixului Safari (DELETE fără
   *    payload), unde apelanții citesc rezultatul ca obiect.
   */
  private async parseSuccessBody<T>(response: Response, isReadOnlyRequest: boolean): Promise<T> {
    if (response.status === 204) return {} as T;
    const text = await response.text();
    if (!text) return (isReadOnlyRequest ? null : {}) as T;
    return JSON.parse(text) as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Upload multipart (FormData) — NU seta Content-Type (browserul pune boundary-ul). Prin `fetchBrut`: la o sesiune
   * expirată, reîmprospătare și o singură reluare (un 401 vine înaintea oricărei lucrări pe server).
   */
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await this.fetchBrut(`${API_URL}/api/v1${endpoint}`, { method: 'POST', body: formData });
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        statusCode: response.status,
        message: 'Eroare la încărcarea fișierului',
      }));
      throw error as ApiError;
    }
    if (response.status === 204) return {} as T;
    return response.json();
  }

  private async tryRefresh(): Promise<RefreshResult> {
    // If a refresh is already in flight, wait for it instead of starting a new one.
    // This prevents the race where multiple parallel 401 responses each trigger
    // their own POST /auth/refresh and blacklist each other's tokens.
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
          credentials: 'include',
        });
        if (!response.ok) return 'failed';
        if (this.onRefreshIdentity) {
          // Backend-ul returnează deja { user, tenant } la refresh — îl folosim
          // pentru reconcilierea identității (vezi comentariul de la hook).
          try {
            const body = await response.clone().json().catch(() => null) as
              { user?: { id?: string }; tenant?: { id?: string } } | null;
            if (body?.user?.id && this.onRefreshIdentity({ userId: body.user.id, tenantId: body.tenant?.id ?? null })) {
              this.sessionChanged = true;
              return 'session-changed';
            }
          } catch { /* reconcilierea e best-effort — refresh-ul rămâne valid */ }
        }
        return 'ok';
      } catch {
        return 'failed';
      }
    })();

    try {
      return await this.refreshPromise;
    } finally {
      // Clear shortly after so a later genuine refresh can run.
      // Small delay lets all concurrent callers observe the same result
      // and benefit from the freshly rotated cookies on their retries.
      setTimeout(() => { this.refreshPromise = null; }, 100);
    }
  }

  /**
   * Reîmprospătarea comună (single-flight, împreună cu cererile `api` și `fetchCuSesiune` din același moment), pentru
   * conexiunile care nu sunt cereri `fetch`: fluxul HoReCa (`EventSource`) o cere înainte să se reconecteze. Cererile
   * brute folosesc `fetchCuSesiune`, care o face singur. Întoarce `true` dacă sesiunea s-a reînnoit pe aceeași
   * identitate; `false` și când cookie-ul poartă alt cont sau altă firmă (pagina se reîncarcă).
   */
  async refreshSession(): Promise<boolean> {
    return (await this.tryRefresh()) === 'ok';
  }

  /**
   * `fetch` brut către API (previzualizări și descărcări de PDF/blob, încărcări, exporturi), cu aceeași regulă de sesiune
   * ca `fetch` de mai sus. Cookie-urile sunt mereu incluse.
   *
   * La 401 (sesiunea expiră o dată pe oră): reîmprospătarea comună (single-flight, împreună cu cererile `api` din
   * același moment), apoi o singură reluare, cu aceleași opțiuni (corpul, metoda, `signal`). Reluarea e sigură și la
   * POST: un 401 de sesiune e dat de gardă înaintea oricărei lucrări pe server.
   *
   * - Cont sau firmă schimbate în alt tab, ori pagina deja plecată pe altă sesiune (și în timpul reîmprospătării):
   *   cererea nu pleacă și nu se reia; aruncă `SESSION_CHANGED`, ca `fetch`, iar pagina se reîncarcă singură.
   * - Reîmprospătare eșuată (sesiunea a murit): la /login, ca `fetch`; apelantul primește 401-ul.
   * - La /auth/* (login, înregistrare, refresh…), 401-ul e chiar răspunsul, ca în `fetch`: fără reîmprospătare.
   *
   * Întoarce răspunsul, ca `fetch`: erorile de după (404, 500, 401-ul nereluat) le tratează apelantul.
   */
  async fetchBrut(input: string, init: RequestInit = {}): Promise<Response> {
    if (this.sessionChanged) throw sessionChangedError();
    const cerere: RequestInit = { ...init, credentials: 'include' };
    const raspuns = await fetch(input, cerere);
    if (raspuns.status !== 401 || isAuthEndpoint(caleaInApi(input))) return raspuns;

    const refreshed = await this.tryRefresh();
    if (refreshed === 'session-changed') throw sessionChangedError();
    if (refreshed === 'ok') {
      if (this.sessionChanged) throw sessionChangedError();
      return fetch(input, cerere);
    }
    if (typeof window !== 'undefined' && !isOnAuthPage()) window.location.href = '/login';
    return raspuns;
  }
}

export const api = new ApiClient();
export type { ApiError };

/**
 * Cererile către API care nu trec prin `api.get/post/...` (PDF, blob, `FormData`, exporturi): `fetch` cu cookie-urile și
 * cu reîmprospătarea sesiunii la 401 (`ApiClient.fetchBrut`). Garda `fetch-cu-sesiune.garda.spec.ts` cere ca toate
 * cererile către API din aplicație să treacă pe aici sau prin `api`.
 */
export function fetchCuSesiune(input: string, init?: RequestInit): Promise<Response> {
  return api.fetchBrut(input, init);
}
